import React from 'react';

import { Calendar, Space, Tooltip } from 'antd';
import Button from 'app/components/Button';
import Dropdown from 'app/components/Dropdown';
import styled from '@emotion/styled';
import { Title } from 'app/components/Typography/Title';
import { DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Caption } from 'app/components/Typography/Caption';
import JobEventScheduleItem from 'app/components/EventItem/JobEventSchedule';
import { IInspectionJobEvent } from 'app/types/interfaces/InspectionJobEvent';
import { StaticContext } from 'app/providers/StaticProvider';
const CalendarHeader = styled.div`
  padding-top: 10px;
  padding-left: 20px;
  > div:first-of-type {
    margin-bottom: 10px;
  }
`;

const WrapperActions = styled.div`
  width: 100%;
  display: flex;
  > div:last-of-type {
    margin: auto;
  }
  h5 {
    margin-bottom: 0;
  }
`;
// 2px solid #f0f0f0
const StyledCalendar = styled(Calendar)`
  .ant-picker-cell {
    > .ant-picker-cell-inner {
      .ant-picker-calendar-date-content {
        height: 130px;
      }
      background: rgba(240, 240, 240, 1);
    }
    &.ant-picker-cell-in-view {
      > .ant-picker-cell-inner {
        background: #ffff;
      }
    }
    &.ant-picker-cell-selected {
      > .ant-picker-cell-inner {
        background-color: #e6f7ff;
      }
    }
    &.ant-picker-cell-today {
      border-left: 5px solid #005eff;
      > .ant-picker-calendar-date-today {
        border-top: 1px solid #f0f0f0;
      }
    }
  }

  .ant-picker-cell {
    height: auto;
    > .ant-picker-cell-inner {
      margin-left: 0;
    }
    &:not(.ant-picker-cell-in-view) {
      > .ant-picker-cell-inner {
        background: rgba(240, 240, 240, 1);
      }
    }
  }

  .ant-picker-calendar-date-value {
    text-align: left;
    font-size: 16px;
    font-weight: 400;
    line-height: 19px;
  }
`;
const ListEvent = styled.ul`
  padding-left: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  li {
    margin: 4px 0;
    list-style: none;
  }
`;

const StyledLabel = styled.a`
  // color: #3a4861;
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.015em;
  text-align: left;
`;
const StyledButtonToday = styled(Button)`
  padding-left: 0;
  > span {
    font-size: 16px;
    font-weight: 500;
    line-height: 19px;
    letter-spacing: 0.015em;
    text-align: left;
  }
`;
const renderHeaderCalendar = (config: {
  value: moment.Moment;
  [key: string]: any;
}) => {
  return (
    <CalendarHeader>
      <Space size={28}>
        <Dropdown.Inspector
          value={config.filteredInspectors}
          onApply={config.onChangeFilterInspectors}
          onReset={() => {
            config.onChangeFilterInspectors(['all']);
          }}
          renderLabel={(labelProps) => {
            return (
              <StyledLabel type="link">
                <Space size={8}>
                  {labelProps.selections?.includes('all')
                    ? 'All Inspectors'
                    : `Inspectors(${labelProps.selections?.length ?? ''})`}
                  <DownOutlined />
                </Space>
              </StyledLabel>
            );
          }}
        ></Dropdown.Inspector>

        <Dropdown.FilterDropdown
          options={[{ value: 'all', label: 'All Locations' }].concat(
            config?.locations ?? []
          )}
          value={config.filteredLocations}
          onApply={config.onChangeFilterLocations}
          onReset={() => {
            config.onChangeFilterLocations(['all']);
          }}
          renderLabel={(labelProps) => {
            return (
              <StyledLabel type="link">
                <Space size={8}>
                  {labelProps.selections?.includes('all')
                    ? 'All Locations'
                    : `Locations(${labelProps.selections?.length ?? ''})`}
                  <DownOutlined />
                </Space>
              </StyledLabel>
            );
          }}
        ></Dropdown.FilterDropdown>
      </Space>
      <WrapperActions>
        <StyledButtonToday
          type="link"
          onClick={() => {
            config.onChange(moment(new Date()));
          }}
        >
          Today
        </StyledButtonToday>

        <Space align="center">
          <Button
            type="text"
            onClick={() => {
              const now = config.value.clone().add(-1, 'months');
              config.onChange(now);
            }}
          >
            <LeftOutlined />
          </Button>
          <Title level={5}>
            <a onClick={(e) => e.preventDefault()}>
              {config.value.format('MMMM YYYY')}
            </a>
          </Title>
          <Button
            type="text"
            onClick={() => {
              const now = config.value.clone().add(1, 'months');
              config.onChange(now);
            }}
          >
            <RightOutlined />
          </Button>
        </Space>
      </WrapperActions>
    </CalendarHeader>
  );
};

const EventCalendar = (props: {
  events: Array<IInspectionJobEvent> | null | undefined;
  value: moment.Moment;
  filtered: {
    inspectorIds: Array<string | number | any>;
    areas: Array<string | number | any>;
    [key: string]: Array<any>;
  };
  onChange: (date: moment.Moment) => void;
  onChangeFilterInspectors: (values: string[] | number[] | any[]) => void;
  onChangeFilterLocations: (values: string[] | number[] | any[]) => void;
  renderLabel?: (values: any) => React.ReactNode;
}) => {
  const {
    events = [],
    filtered,
    onChangeFilterInspectors,
    onChangeFilterLocations,
    renderLabel
  } = props;
  const { countries } = React.useContext(StaticContext);
  const options = React.useMemo(() => {
    return countries.map((country) => {
      return {
        value: country.alpha3,
        label: country?.country
      };
    });
  }, [countries]);
  const getEventByDate = (date: moment.Moment) => {
    if (!events || !events.length) return [];
    return events.filter(
      (event) =>
        moment(event.inspectionDate).format('YYYYMMDD') ===
        date.format('YYYYMMDD')
    );
  };
  return (
    <StyledCalendar
      value={props.value}
      onChange={props.onChange}
      onPanelChange={props.onChange}
      headerRender={(config) => {
        return renderHeaderCalendar({
          ...config,
          filteredInspectors: filtered.inspectorIds,
          filteredLocations: filtered.areas,
          onChangeFilterInspectors,
          onChangeFilterLocations,
          locations: options
        });
      }}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      dateCellRender={(_date) => {
        const inspectionEvents = getEventByDate(_date);
        const list = inspectionEvents.slice(0, 3);
        const remaining = inspectionEvents.slice(3, inspectionEvents.length);
        return (
          <ListEvent>
            {list.map(({ inspection, inspector, ...rest }, index) => {
              return (
                <li
                  key={index}
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    fontSize: 10
                  }}
                >
                  <span>
                    <JobEventScheduleItem
                      title=""
                      status={inspection?.status as any}
                      inspectorName={`${inspector?.firstName} ${inspector?.lastName}`}
                    ></JobEventScheduleItem>
                    {renderLabel
                      ? renderLabel({ ...rest, inspection, inspector })
                      : inspection?.factory?.name}
                  </span>
                </li>
              );
            })}
            {remaining?.length ? (
              <li>
                <Tooltip trigger={['hover']}>
                  <Caption
                    style={{
                      fontSize: 12
                    }}
                  >
                    +{remaining?.length} more
                  </Caption>
                </Tooltip>
              </li>
            ) : (
              <></>
            )}
          </ListEvent>
        );
      }}
    ></StyledCalendar>
  );
};

export default EventCalendar;
